/**
 * HealthTopicDetail — /health/topics/:kind/:name
 * On first visit, POSTs to /api/health/topics/kick to generate an AI
 * article via Claude Sonnet 4.5. Content is cached forever after.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, ShieldAlert, Sparkles, AlertTriangle } from "lucide-react";
import { http } from "@/lib/api";
import HealthCategoryNav from "@/components/HealthCategoryNav";

const KIND_LABELS = {
  disease: "Disease / Condition", symptom: "Symptom", procedure: "Procedure",
  drug: "Drug", wellness: "Wellness Topic", mental: "Mental Health Topic",
  traditional: "Traditional Medicine",
};

export default function HealthTopicDetail() {
  const { kind, name } = useParams();
  const decoded = decodeURIComponent(name || "");
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await http.post("/health/topics/kick", { kind, name: decoded });
        if (!cancelled) setTopic(r.data?.topic || null);
        if (!r.data?.ok && r.data?.topic?.status === "failed") {
          setError("AI generation failed. Try again later.");
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to load topic.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [kind, decoded]);

  return (
    <>
      <HealthCategoryNav />
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="topic-detail">
        <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-2">
          {KIND_LABELS[kind] || kind}
        </div>
        <h1 className="font-display text-4xl mb-3">{decoded}</h1>
        <div className="text-xs opacity-70 inline-flex items-start gap-1.5 mb-6">
          <ShieldAlert className="w-3.5 h-3.5 mt-0.5"/>
          {topic?.disclaimer || "Educational only — not medical advice. Always consult a qualified healthcare professional."}
        </div>

        {loading && (
          <div className="wofy-card !p-6 opacity-80 inline-flex items-center gap-2" data-testid="topic-loading">
            <Loader2 className="w-4 h-4 animate-spin"/>
            Generating an AI-written article… (first visit only, may take up to a minute)
          </div>
        )}

        {!loading && error && (
          <div className="wofy-card !p-6 border" style={{ borderColor: "#B45309", background: "#FEF3C7" }}>
            <div className="inline-flex items-center gap-2 mb-1"><AlertTriangle className="w-4 h-4"/> {error}</div>
          </div>
        )}

        {!loading && topic?.body && (
          <TopicBody kind={kind} body={topic.body} />
        )}

        {!loading && topic && !topic.body && !error && (
          <div className="wofy-card !p-6 opacity-80">Article is being prepared. Please reload in a moment.</div>
        )}
      </div>
    </>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-6">
      <h2 className="font-display text-xl mb-2">{title}</h2>
      {children}
    </section>
  );
}

function List({ items }) {
  if (!items?.length) return null;
  return <ul className="list-disc ml-5 space-y-1 opacity-90">{items.map((s, i) => <li key={i}>{s}</li>)}</ul>;
}

function FAQs({ faqs }) {
  if (!faqs?.length) return null;
  return (
    <div className="space-y-3">
      {faqs.map((f, i) => (
        <div key={i} className="wofy-card !p-4">
          <div className="font-medium mb-1">{f.q}</div>
          <p className="opacity-80 text-sm">{f.a}</p>
        </div>
      ))}
    </div>
  );
}

function TopicBody({ kind, body }) {
  if (kind === "disease" || kind === "mental") {
    return (
      <div data-testid="topic-body">
        {body.overview && <Section title="Overview"><p className="opacity-90 whitespace-pre-line">{body.overview}</p></Section>}
        {body.symptoms         && <Section title="Symptoms"><List items={body.symptoms}/></Section>}
        {body.signs_and_symptoms && <Section title="Signs & Symptoms"><List items={body.signs_and_symptoms}/></Section>}
        {body.causes           && <Section title="Causes"><List items={body.causes}/></Section>}
        {body.risk_factors     && <Section title="Risk Factors"><List items={body.risk_factors}/></Section>}
        {body.prevention       && <Section title="Prevention"><List items={body.prevention}/></Section>}
        {body.diagnosis        && <Section title="Diagnosis"><List items={body.diagnosis}/></Section>}
        {body.treatment        && <Section title="Treatment (general categories)"><List items={body.treatment}/></Section>}
        {body.self_care        && <Section title="Self-care"><List items={body.self_care}/></Section>}
        {body.types_of_support && <Section title="Types of professional support"><List items={body.types_of_support}/></Section>}
        {body.complications    && <Section title="Possible complications"><List items={body.complications}/></Section>}
        {body.recovery         && <Section title="Recovery"><List items={body.recovery}/></Section>}
        {body.lifestyle        && <Section title="Lifestyle management"><List items={body.lifestyle}/></Section>}
        {body.when_to_seek_care && <Section title="When to seek care"><p className="opacity-90 whitespace-pre-line" style={{ color: "var(--wofy-primary)" }}>{body.when_to_seek_care}</p></Section>}
        {body.when_to_seek_help && <Section title="When to seek help"><p className="opacity-90 whitespace-pre-line" style={{ color: "var(--wofy-primary)" }}>{body.when_to_seek_help}</p></Section>}
        {body.related_specialists && <Section title="Related specialists"><List items={body.related_specialists}/></Section>}
        {body.faqs && <Section title="Frequently asked questions"><FAQs faqs={body.faqs}/></Section>}
      </div>
    );
  }
  if (kind === "symptom") {
    return (
      <div data-testid="topic-body">
        {body.overview && <Section title="Overview"><p className="opacity-90 whitespace-pre-line">{body.overview}</p></Section>}
        {body.common_causes      && <Section title="Common causes"><List items={body.common_causes}/></Section>}
        {body.less_common_causes && <Section title="Less common causes"><List items={body.less_common_causes}/></Section>}
        {body.self_care          && <Section title="Self-care"><List items={body.self_care}/></Section>}
        {body.when_to_seek_care  && <Section title="When to seek care"><p className="opacity-90 whitespace-pre-line" style={{ color: "var(--wofy-primary)" }}>{body.when_to_seek_care}</p></Section>}
        {body.related_specialists && <Section title="Related specialists"><List items={body.related_specialists}/></Section>}
        {body.faqs && <Section title="Frequently asked questions"><FAQs faqs={body.faqs}/></Section>}
      </div>
    );
  }
  if (kind === "procedure") {
    return (
      <div data-testid="topic-body">
        {body.overview     && <Section title="Overview"><p className="opacity-90 whitespace-pre-line">{body.overview}</p></Section>}
        {body.purpose      && <Section title="Purpose"><List items={body.purpose}/></Section>}
        {body.preparation  && <Section title="Preparation"><List items={body.preparation}/></Section>}
        {body.how_its_done && <Section title="How it's done"><List items={body.how_its_done}/></Section>}
        {body.risks        && <Section title="Risks"><List items={body.risks}/></Section>}
        {body.recovery     && <Section title="Recovery"><List items={body.recovery}/></Section>}
        {body.faqs && <Section title="Frequently asked questions"><FAQs faqs={body.faqs}/></Section>}
      </div>
    );
  }
  if (kind === "drug") {
    return (
      <div data-testid="topic-body">
        <div className="grid gap-3 sm:grid-cols-2 mb-6">
          {body.generic_name && <div className="wofy-card !p-3"><div className="text-xs opacity-70">Generic name</div><div className="font-medium">{body.generic_name}</div></div>}
          {body.drug_class  && <div className="wofy-card !p-3"><div className="text-xs opacity-70">Drug class</div><div className="font-medium">{body.drug_class}</div></div>}
        </div>
        {body.brand_names   && <Section title="Brand names"><List items={body.brand_names}/></Section>}
        {body.uses          && <Section title="Uses"><List items={body.uses}/></Section>}
        {body.dosage_forms  && <Section title="Dosage forms"><List items={body.dosage_forms}/></Section>}
        {body.administration && <Section title="Administration"><p className="opacity-90 whitespace-pre-line">{body.administration}</p></Section>}
        {body.common_side_effects && <Section title="Common side effects"><List items={body.common_side_effects}/></Section>}
        {body.serious_side_effects && <Section title="Serious side effects"><List items={body.serious_side_effects}/></Section>}
        {body.warnings         && <Section title="Warnings"><List items={body.warnings}/></Section>}
        {body.interactions     && <Section title="Drug interactions"><List items={body.interactions}/></Section>}
        {body.contraindications && <Section title="Contraindications"><List items={body.contraindications}/></Section>}
        {body.pregnancy_notes  && <Section title="Pregnancy notes"><p className="opacity-90 whitespace-pre-line">{body.pregnancy_notes}</p></Section>}
        {body.storage          && <Section title="Storage"><p className="opacity-90 whitespace-pre-line">{body.storage}</p></Section>}
        {body.faqs && <Section title="Frequently asked questions"><FAQs faqs={body.faqs}/></Section>}
      </div>
    );
  }
  if (kind === "wellness") {
    return (
      <div data-testid="topic-body">
        {body.overview     && <Section title="Overview"><p className="opacity-90 whitespace-pre-line">{body.overview}</p></Section>}
        {body.benefits     && <Section title="Benefits"><List items={body.benefits}/></Section>}
        {body.how_to_start && <Section title="How to start"><List items={body.how_to_start}/></Section>}
        {body.tips         && <Section title="Tips"><List items={body.tips}/></Section>}
        {body.cautions     && <Section title="Cautions"><List items={body.cautions}/></Section>}
        {body.faqs && <Section title="Frequently asked questions"><FAQs faqs={body.faqs}/></Section>}
      </div>
    );
  }
  if (kind === "traditional") {
    return (
      <div data-testid="topic-body">
        {body.overview          && <Section title="Overview"><p className="opacity-90 whitespace-pre-line">{body.overview}</p></Section>}
        {body.history           && <Section title="History"><List items={body.history}/></Section>}
        {body.principles        && <Section title="Principles"><List items={body.principles}/></Section>}
        {body.common_practices  && <Section title="Common practices"><List items={body.common_practices}/></Section>}
        {body.evidence_notes    && <Section title="Evidence & scientific notes"><p className="opacity-90 whitespace-pre-line">{body.evidence_notes}</p></Section>}
        {body.cautions          && <Section title="Cautions"><List items={body.cautions}/></Section>}
        {body.faqs && <Section title="Frequently asked questions"><FAQs faqs={body.faqs}/></Section>}
      </div>
    );
  }
  return <pre className="wofy-card !p-4 text-xs overflow-auto">{JSON.stringify(body, null, 2)}</pre>;
}
